import { linkedInProfileUrlFrom, normalizeLinkedInInput } from './linkedin-qr';

/**
 * The live-camera QR scan loop — U12 / R12 of
 * plans/2026-08-25-002-lead-scanner-round2-plan.md.
 *
 * This module is the framework-free half of the in-app LinkedIn QR scanner:
 * it owns the repeat-until-you-see-a-LinkedIn-profile loop and nothing else.
 * Every seam it needs is injected — where frames come from (`grabFrame`),
 * what reads them (`decode`), and what drives the ticks (`schedule`) — which
 * is what makes stop-on-first-valid, ignore-non-LinkedIn, and
 * swallow-a-decode-throw assertable without a camera, without the zxing WASM
 * binary, and without real timers (`__tests__/camera-qr.test.ts`). Same
 * split, and the same reason, as `photo-intake.ts` vs
 * `components/PhotoIntake.tsx`.
 *
 * `components/LinkedInQrScanner.tsx` is the thin React/DOM wiring over this:
 * it opens the `MediaStream`, paints `<video>` frames into an offscreen
 * canvas to produce the `ImageData` this loop asks for, and calls `cancel()`
 * from its effect cleanup.
 */

/**
 * Why the viewfinder can't run. Split by CAUSE rather than collapsed into
 * one "camera unavailable" because the rep's next action differs per case —
 * re-grant a permission, move to an https URL, quit the video call that's
 * holding the camera — and the component renders a different hint for each
 * (`components/LinkedInQrScanner.tsx`).
 */
export type CameraUnavailableReason =
  | 'permission-denied'
  | 'no-camera'
  | 'camera-busy'
  | 'insecure-context'
  | 'unknown';

/**
 * Whether it's worth asking for the camera at all, decided before any
 * permission prompt. `null` means "go ahead and try".
 *
 * Takes the two facts rather than reading `window`/`navigator` itself, so
 * the branch that matters most — an insecure context — is assertable without
 * a browser. It matters most because it's the one a rep hits by accident and
 * cannot fix with a permission toggle: `navigator.mediaDevices` is simply
 * absent outside a secure context, and `localhost` counts as secure while a
 * LAN IP (`http://192.168.x.x:5173`, exactly how a phone reaches a dev
 * machine) does not.
 */
export function preflightCameraUnavailableReason({
  isSecureContext,
  hasGetUserMedia,
}: {
  isSecureContext: boolean;
  hasGetUserMedia: boolean;
}): CameraUnavailableReason | null {
  // Checked FIRST: an insecure context is why `getUserMedia` is missing in
  // the common case, and reporting "no camera found" for it would send the
  // rep hunting for a hardware problem that isn't there.
  if (!isSecureContext) return 'insecure-context';
  if (!hasGetUserMedia) return 'no-camera';
  return null;
}

/**
 * Classifies a `getUserMedia` rejection.
 *
 * Matches on `DOMException.name` only — the `name` values are specified,
 * while `message` is browser-specific prose that changes between releases.
 * Anything unrecognized (including a non-`Error` throw) is `unknown`, whose
 * copy is deliberately "try again, or use a photo instead" rather than a
 * guess at a cause.
 */
export function getUserMediaFailureReason(err: unknown): CameraUnavailableReason {
  const name = err instanceof Error ? err.name : '';
  switch (name) {
    // The rep dismissed the prompt, or the site is blocked in browser
    // settings. `SecurityError` is Safari's older spelling of the same.
    case 'NotAllowedError':
    case 'PermissionDeniedError':
    case 'SecurityError':
      return 'permission-denied';
    // No device at all, or none that can satisfy `facingMode` — the same
    // dead end from the rep's side.
    case 'NotFoundError':
    case 'DevicesNotFoundError':
    case 'OverconstrainedError':
      return 'no-camera';
    // A device exists but the OS won't hand it over: another tab or app
    // (usually a video call) has it.
    case 'NotReadableError':
    case 'TrackStartError':
      return 'camera-busy';
    default:
      return 'unknown';
  }
}

/** What the loop reads a frame with. `null` means "no frame available yet" —
 *  the normal state for the first few ticks while the `<video>` element is
 *  still negotiating dimensions with the camera — and costs nothing: a null
 *  frame is skipped without a decode call. */
export type FrameGrabber = () => ImageData | null;

/** `decodeQrFromImageData` from `qr-decode.ts` in the app; a stub in tests.
 *  Resolves to the RAW decoded payload (whatever the QR carried) or `null`
 *  when the frame holds no readable QR — the loop, not the decoder, decides
 *  what a payload means, exactly as `photo-intake.ts` does for the
 *  still-photo path. */
export type ImageDataDecoder = (frame: ImageData) => Promise<string | null>;

/**
 * The timer seam. Called once with the loop's tick function; returns the
 * canceller for whatever it scheduled.
 *
 * Returning its own canceller (rather than exposing a handle) is what keeps
 * the timer type out of this module's contract: `setInterval`'s return is a
 * `number` in the DOM and a `Timeout` object in Node/Bun, and the loop has
 * no reason to care which.
 */
export type IntervalScheduler = (tick: () => void, intervalMs: number) => () => void;

export interface CameraQrLoopOptions {
  grabFrame: FrameGrabber;
  decode: ImageDataDecoder;
  /** Fired at most ONCE, with the canonical
   *  `https://www.linkedin.com/in/<slug>` URL, immediately after the loop
   *  has stopped itself. */
  onResult: (linkedInUrl: string) => void;
  /** Defaults to {@link CAMERA_QR_DECODE_INTERVAL_MS}. */
  intervalMs?: number;
  /** Defaults to a real `setInterval`/`clearInterval` pair. */
  schedule?: IntervalScheduler;
}

export interface CameraQrLoop {
  /** Stops the loop. Idempotent, and safe to call from a React effect
   *  cleanup while a decode is still in flight: a decode that resolves after
   *  `cancel()` is discarded rather than delivered. */
  cancel: () => void;
}

/**
 * Decode cadence. Within the plan's 150-250ms band, chosen at the middle:
 * fast enough that a rep pointing a phone at a badge sees a hit as soon as
 * the frame is sharp, slow enough that a `tryHarder` zxing pass (which is
 * the expensive part, and deliberately kept identical to the still-photo
 * path's options) has room to finish before the next tick would fire. Ticks
 * that arrive while a decode is still running are dropped, so this is a
 * floor on the decode rate, not a guarantee of it.
 */
export const CAMERA_QR_DECODE_INTERVAL_MS = 200;

const defaultScheduler: IntervalScheduler = (tick, intervalMs) => {
  const handle = setInterval(tick, intervalMs);
  return () => clearInterval(handle);
};

/**
 * The LinkedIn profile URL a decoded frame identifies, or `null` if the
 * payload isn't one.
 *
 * The two-step composition is deliberate and the order matters:
 *
 *  1. {@link linkedInProfileUrlFrom} is the GATE. Conference badges routinely
 *     carry the event's own registration-system QR (and vCards, and wifi
 *     strings), none of which is an identity — U5's rejection rule, applied
 *     unchanged on this path.
 *  2. {@link normalizeLinkedInInput} then canonicalizes what got through
 *     (§7's tracking-param strip, per U6).
 *
 * Normalizing FIRST would be wrong, not merely redundant:
 * `normalizeLinkedInInput` treats a bare slug-shaped string as a slug, so a
 * registration code like `EVT2026REG88213` would be promoted into a
 * LinkedIn URL that points at nobody. The gate runs first so only real
 * profile URLs are ever normalized.
 */
export function linkedInUrlFromQrPayload(payload: string): string | null {
  const gated = linkedInProfileUrlFrom(payload);
  if (gated === null) return null;
  return normalizeLinkedInInput(gated);
}

/**
 * Runs `decode(grabFrame())` on an interval until a frame yields a LinkedIn
 * profile QR, then stops itself and reports the canonical URL exactly once.
 *
 * Everything that isn't that outcome is a no-op frame the loop keeps going
 * past: no frame yet, no QR in the frame, a QR that isn't a LinkedIn profile
 * (a registration code), or a decoder that throws — a viewfinder pointed at
 * a badge produces a stream of all four, and any of them ending the scan
 * would be a bug, not a failure to report.
 */
export function startCameraQrLoop({
  grabFrame,
  decode,
  onResult,
  intervalMs = CAMERA_QR_DECODE_INTERVAL_MS,
  schedule = defaultScheduler,
}: CameraQrLoopOptions): CameraQrLoop {
  // Plain closure variables, not refs: this is not a React module, and the
  // whole point of the `CameraQrLoop` handle is that its lifetime is the
  // caller's effect lifetime. There is no lifetime state that outlives a
  // call, which is what makes the component's cleanup StrictMode-safe by
  // construction (docs/v4-system/strictmode-resilient-effects.md — the
  // hazard there is a ref reset only in cleanup; a fresh loop object per
  // `startCameraQrLoop` call cannot have that shape).
  let stopped = false;
  // A `tryHarder` decode can outlast one interval. Overlapping decodes would
  // queue up behind the WASM module and make the viewfinder progressively
  // laggier, so a tick that lands mid-decode is dropped rather than queued.
  let decoding = false;

  const cancelInterval = schedule(() => {
    void tick();
  }, intervalMs);

  function stop() {
    if (stopped) return;
    stopped = true;
    cancelInterval();
  }

  async function tick(): Promise<void> {
    if (stopped || decoding) return;

    const frame = grabFrame();
    if (frame === null) return;

    decoding = true;
    let payload: string | null = null;
    try {
      payload = await decode(frame);
    } catch {
      // A decode failure is a frame with no QR in it, full stop: a missing
      // WASM binary, a zero-sized frame mid-resize, and a corrupt buffer are
      // all "keep looking" from the viewfinder's point of view. Same stance
      // as `qr-decode.ts`'s own never-throw contract, restated here because
      // an injected decoder in a test (or a future non-zxing decoder) is not
      // bound by it.
      payload = null;
    } finally {
      decoding = false;
    }

    // `cancel()` may have landed while the decode above was in flight (the
    // rep closed the scanner, or the component unmounted) — deliver nothing.
    if (stopped) return;

    const linkedInUrl = payload === null ? null : linkedInUrlFromQrPayload(payload);
    if (linkedInUrl === null) return;

    // Stop BEFORE reporting: `onResult` synchronously navigates to the
    // review screen in the real caller, and a further tick firing behind
    // that would decode against a torn-down canvas.
    stop();
    onResult(linkedInUrl);
  }

  return { cancel: stop };
}
